import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import BuildProGame from './components/BuildProGame';
import StartScreen from './components/StartScreen';
import Leaderboard from './components/Leaderboard';
import HowToPlay from './components/HowToPlay';
import { GameHeader, fetchGameStatuses } from '@precisionpros/game-ui';
import { createGameLinks, createLeaderboardHeaderLinks } from './gameLinks';
import './App.css';

const installHubAuthFetch = () => {
  if (typeof window === 'undefined' || window.__hubAuthFetchInstalled) return;
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const token = new URLSearchParams(window.location.search).get('hub_token') || localStorage.getItem('hubToken') || '';
    if (token) localStorage.setItem('hubToken', token);
    const headers = new Headers(init.headers || {});
    if (token) headers.set('X-Hub-Token', token);
    return originalFetch(input, { ...init, headers });
  };
  window.__hubAuthFetchInstalled = true;
};

installHubAuthFetch()

const GAME_LINKS = [
  { key: 'wordpro', label: 'W', name: 'WordPro', href: 'https://wordpro.precisionpros.com' },
  { key: 'connectpro', label: 'C', name: 'ConnectPro', href: 'https://connectpro.precisionpros.com' },
  { key: 'eventpro', label: 'E', name: 'EventPro', href: 'https://eventpro.precisionpros.com' },
  { key: 'wordprofinder', label: 'WF', name: 'WordProFinder', href: 'https://wordprofinder.precisionpros.com' },
  { key: 'seashroud', label: 'S', name: 'Sea Shroud', href: 'https://seashroud.precisionpros.com' },
  { key: 'sevenapart', label: '7', name: '7 Apart', href: 'https://sevenapart.precisionpros.com' },
  { key: 'mathpro', label: 'M', name: 'MathPro', href: 'https://mathpro.precisionpros.com' }
];

const LOGIN_URL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://localhost:3000' : 'https://games.precisionpros.com';
const PLAYABLE_ROUTES = new Set(['/', '/game']);

const isLocalDevHost = (hostname) => (
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname === '::1' ||
  hostname.startsWith('10.') ||
  hostname.startsWith('192.168.') ||
  /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
);

const getLocalGameLinks = () => {
  if (typeof window === 'undefined') return GAME_LINKS;

  const isLocalDev = isLocalDevHost(window.location.hostname);
  if (!isLocalDev) return GAME_LINKS;

  return [
    { key: 'wordpro', label: 'W', name: 'WordPro', href: 'http://localhost:3005' },
    { key: 'connectpro', label: 'C', name: 'ConnectPro', href: 'http://localhost:3001' },
    { key: 'eventpro', label: 'E', name: 'EventPro', href: 'http://localhost:3002' },
    { key: 'wordprofinder', label: 'WF', name: 'WordProFinder', href: 'http://localhost:3004' },
    { key: 'seashroud', label: 'S', name: 'Sea Shroud', href: 'http://localhost:3006' },
    { key: 'sevenapart', label: '7', name: '7 Apart', href: `http://${window.location.hostname}:3007` },
    { key: 'mathpro', label: 'M', name: 'MathPro', href: `http://${window.location.hostname}:3008` }
  ];
};

const getHomeUrl = () => {
  if (typeof window !== 'undefined' && isLocalDevHost(window.location.hostname)) {
    return 'http://localhost:3000';
  }

  return 'https://games.precisionpros.com';
};

const getInitialPlayerName = () => {
  if (typeof window === 'undefined') return '';

  const params = new URLSearchParams(window.location.search);
  return (
    params.get('player_name') ||
    localStorage.getItem('playerName') ||
    localStorage.getItem('buildpro_player_name') ||
    ''
  );
};

const shouldRedirectToUnifiedLogin = () => {
  if (typeof window === 'undefined') return false;

  const { hostname, pathname, search } = window.location;
  const params = new URLSearchParams(search);
  const hasHubToken = params.get('hub_token') || localStorage.getItem('hubToken');
  const isLocalDev = isLocalDevHost(hostname);

  return !(process.env.NODE_ENV === 'development' && isLocalDev)
    && !hasHubToken && PLAYABLE_ROUTES.has(pathname);
};

const initialPage = () => {
  const path = window.location.pathname;
  if (path === '/how-to-play') return 'how-to-play';
  if (path === '/leaderboard') return 'leaderboard';
  if (path === '/game') return 'game';
  return 'home';
};

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [playerName, setPlayerName] = useState(getInitialPlayerName);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getInitialPlayerName());
  const [otherGamesStatus, setOtherGamesStatus] = useState({});
  const [statusReady, setStatusReady] = useState(false);

  useEffect(() => {
    if (shouldRedirectToUnifiedLogin()) {
      window.location.replace(LOGIN_URL);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paramName = params.get('player_name');

    if (paramName) {
      localStorage.setItem('playerName', paramName);
      localStorage.setItem('buildpro_player_name', paramName);
      setPlayerName(paramName);
      setIsAuthenticated(true);
    }
  }, [location.search]);

  useEffect(() => {
    const path = location.pathname;
    if (path === '/how-to-play') setCurrentPage('how-to-play');
    else if (path === '/leaderboard') setCurrentPage('leaderboard');
    else if (path === '/game') setCurrentPage('game');
    else setCurrentPage('home');
  }, [location.pathname, isAuthenticated]);

  const handleLogin = (name) => {
    const trimmedName = name.trim();
    localStorage.setItem('playerName', trimmedName);
    localStorage.setItem('buildpro_player_name', trimmedName);
    setPlayerName(trimmedName);
    setIsAuthenticated(true);
    setCurrentPage('home');
    navigate(`/${location.search}`);
  };

  useEffect(() => {
    const hasHubToken = new URLSearchParams(window.location.search).get('hub_token') || localStorage.getItem('hubToken');
    if (!playerName && !hasHubToken) {
      setOtherGamesStatus({});
      setStatusReady(true);
      return;
    }

    setStatusReady(false);
    fetchGameStatuses()
      .then((statuses) => setOtherGamesStatus(statuses))
      .catch(() => setOtherGamesStatus({}))
      .finally(() => setStatusReady(true));
  }, [playerName]);

  const gameLinks = useMemo(() => {
    return createGameLinks(getLocalGameLinks(), otherGamesStatus, playerName);
  }, [otherGamesStatus, playerName]);

  const handleAlreadyPlayed = useCallback(() => {
    navigate(`/${location.search}`);
  }, [location.search, navigate]);

  const navigateWithSearch = useCallback((pathname) => {
    navigate(`${pathname}${location.search}`);
  }, [location.search, navigate]);

  const displayedGameLinks = statusReady ? gameLinks : [];
  const buildStatusLink = {
    key: 'buildpro', label: 'B', letter: 'B', color: '#0f766e',
    href: otherGamesStatus.buildpro ? '/leaderboard' : '/',
    played: otherGamesStatus.buildpro === true,
  };
  const ShellHeader = () => <GameHeader game={{ name: 'Build', wordmarkMain: 'Build', wordmarkAccent: 'Pro', badge: 'B', accent: '#0f766e' }} title={currentPage === 'leaderboard' ? 'Leaderboard' : ''} links={displayedGameLinks.map((game) => ({ ...game, letter: game.label, color: { wordpro: '#4CAF50', connectpro: '#2563eb', eventpro: '#ea580c', wordprofinder: '#4CAF50', seashroud: '#287f89', sevenapart: '#644a83', mathpro: '#996128' }[game.key] }))} homeHref={getHomeUrl()} />;

  const renderPage = () => {
    switch (currentPage) {
      case 'game':
        return isAuthenticated ? (
          <BuildProGame
            playerName={playerName}
            onLeaderboard={() => navigateWithSearch('/leaderboard')}
            onHowToPlay={() => navigateWithSearch('/how-to-play')}
            onAlreadyPlayed={handleAlreadyPlayed}
            onPlayAgain={() => window.location.reload()}
          />
        ) : (
          <StartScreen
            onLogin={handleLogin}
            onHowToPlay={() => navigateWithSearch('/how-to-play')}
            onLeaderboard={() => navigateWithSearch('/leaderboard')}
          />
        );
      case 'home':
        return (
          <StartScreen
            onLogin={handleLogin}
            onHowToPlay={() => navigateWithSearch('/how-to-play')}
            onLeaderboard={() => navigateWithSearch('/leaderboard')}
            onStartGame={() => navigateWithSearch('/game')}
            playerName={playerName}
            isAuthenticated={isAuthenticated}
          />
        );
      case 'leaderboard':
        return (
          <Leaderboard
            onBack={() => navigateWithSearch(isAuthenticated ? '/' : '/')}
            onHowToPlay={() => navigateWithSearch('/how-to-play')}
            playerName={playerName}
            headerLinks={statusReady ? createLeaderboardHeaderLinks(gameLinks, buildStatusLink) : []}
            homeHref={getHomeUrl()}
            centralStatusReady={statusReady}
            centralStatusPlayed={otherGamesStatus.buildpro === true}
          />
        );
      case 'how-to-play':
        return (
          <HowToPlay
            onBack={() => navigateWithSearch(isAuthenticated ? '/' : '/')}
            onLeaderboard={() => navigateWithSearch('/leaderboard')}
          />
        );
      default:
        return (
          <StartScreen
            onLogin={handleLogin}
            onHowToPlay={() => navigateWithSearch('/how-to-play')}
            onLeaderboard={() => navigateWithSearch('/leaderboard')}
            onStartGame={() => navigateWithSearch('/game')}
            playerName={playerName}
            isAuthenticated={isAuthenticated}
          />
        );
    }
  };

  const showShellHeader = currentPage !== 'leaderboard' && !(currentPage === 'home' && isAuthenticated);

  return (
    <div className="App">
      {showShellHeader && <div className="app-header-frame"><ShellHeader /></div>}
      <main>{renderPage()}</main>
    </div>
  );
}

export default App;
